/**
 * SMS opt-out ("STOP") state for Settings → Security, as plain functions so the drift guard
 * (`test/sms-opt-out.guard.spec.ts`) can run them under node without rendering React.
 *
 * When someone replies STOP to a Vera text, Twilio refuses every later text to that number
 * until they text START. The API records it on the factor (`optedOutAt`) and clears it on the
 * next text that goes through. These functions decide what the factor list shows for it.
 */

/** The minimal factor shape these rules read — a subset of `AuthFactor` (hooks/api/useAuthFactors). */
export interface FactorStatusInput {
  type: 'sms' | 'totp'
  verified: boolean
  allowed?: boolean
  optedOutAt?: string | null
}

/** The warning badge on an SMS factor whose number replied STOP. */
export const SMS_BLOCKED_BADGE = 'Texts are blocked (replied STOP)'

export const isSmsBlocked = (factor: FactorStatusInput): boolean => factor.type === 'sms' && Boolean(factor.optedOutAt)

/** The badge text for a factor row, or null when there is nothing to warn about. */
export const factorWarningBadge = (factor: FactorStatusInput): string | null =>
  isSmsBlocked(factor) ? SMS_BLOCKED_BADGE : null

export interface AuthenticatorPrompt {
  severity: 'warning' | 'info'
  title: string
  description: string
}

/**
 * "Add an authenticator app" when text messages are the user's ONLY usable sign-in method and
 * they may add one. Stronger (warning) when those texts are blocked: then the only ways in are
 * a recovery code or texting START. Null otherwise.
 *
 * Adding the authenticator goes through the existing step-up rules (`requireStepUpOr`) — the
 * step-up accepts a recovery code, which is what a user with blocked texts has.
 */
export const authenticatorPrompt = (
  factors: FactorStatusInput[],
  allowedMethods: readonly string[],
): AuthenticatorPrompt | null => {
  const usable = factors.filter((f) => f.verified && f.allowed !== false)
  if (usable.length !== 1 || usable[0].type !== 'sms') return null
  if (!allowedMethods.includes('totp')) return null

  return isSmsBlocked(usable[0])
    ? {
        severity: 'warning',
        title: 'Texts to your phone are blocked',
        description:
          "Your number replied STOP, so we can't text you sign-in codes. Add an authenticator app so you don't get locked out, or reply START to any message from Vera to turn texts back on.",
      }
    : {
        severity: 'info',
        title: 'Add a backup sign-in method',
        description:
          'Text messages are your only sign-in method. Add an authenticator app so you can still sign in if texts stop reaching you.',
      }
}
